/**
 * Course timing, in one place. Every duration in this codebase is MINUTES.
 *
 * It was read as hours in three places — `CourseCard`'s local `formatDuration`,
 * and two spots in `CourseViewPage` that printed `{course.duration} hours` and
 * `{course.duration}h` directly. With the old template value of 120 that rendered
 * "120 hours", which was wrong but survived because every course said the same
 * thing. Now that durations are derived from real lesson content the longest
 * course is 750 minutes, and the same bug renders "750 hours".
 *
 * Reading the value as hours anywhere is a bug. Import from here instead.
 *
 * The session model mirrors `backend/src/scripts/recalibrateCourses.ts` — change
 * one and change the other, or the sales page and the stored duration stop
 * agreeing. A sitting is capped at 25 minutes because adult sustained attention
 * falls off around 20 and course completion rates drop sharply past 15; a week is
 * five sittings, which leaves slack for a missed day.
 */

const SESSION_CAP_MINUTES = 25;
const SESSIONS_PER_WEEK = 5;

/**
 * Fallback session length, used when a view has the course total but not its
 * individual lessons. It is the mean sitting length across the real catalogue,
 * not a guess — packing whole lessons under a 25-minute cap lands here because
 * the median lesson is ~9.5 minutes, so most sittings are two lessons.
 */
const TYPICAL_SESSION_MINUTES = 22;

/** Above this, a course stops being something you finish in one go. */
const SINGLE_SITTING_CEILING_MINUTES = 150;

export interface CourseTimingParts {
  /** "10h 34m" — total content time. */
  total: string;
  /** "44 lessons · 5 modules", omitting either half when it is unknown. */
  structure: string;
  /** "8 weeks · 18 min a day", or "5 days · 20 min a day". */
  schedule: string;
  /** Sittings a learner doing one a day would need. */
  sessions: number;
  /** Those sittings at five a week — the unit course cards are measured in. */
  weeks: number;
  /** "18 min a day" — the pace the weeks assume. */
  pace: string;
  /** Whether the whole thing fits in an afternoon. */
  isSingleSitting: boolean;
}

interface LessonLike {
  duration?: number;
}

interface ModuleLike {
  lessons?: LessonLike[];
}

/** What every function here needs of a course, whether it came from a list
 *  endpoint (counts, no modules) or a detail one (modules, real lesson times). */
export interface TimedCourse {
  duration?: number | null;
  modules?: ModuleLike[];
  moduleCount?: number;
  lessonCount?: number;
}

export const formatDuration = (minutes?: number | null): string => {
  if (!minutes || minutes <= 0) return 'N/A';

  const rounded = Math.round(minutes);
  const hours = Math.floor(rounded / 60);
  const remainder = rounded % 60;

  if (!hours) return `${remainder}m`;
  return remainder ? `${hours}h ${remainder}m` : `${hours}h`;
};

/**
 * Packs lessons into day-sized sittings. A lesson is never split across two days,
 * so a learner always stops at a boundary rather than mid-explanation.
 */
const packSessions = (lessonMinutes: number[]): number[] => {
  const days: number[] = [];
  let current = 0;

  for (const minutes of lessonMinutes) {
    if (current > 0 && current + minutes > SESSION_CAP_MINUTES) {
      days.push(current);
      current = 0;
    }
    current += minutes;
  }
  if (current > 0) days.push(current);

  return days;
};

const collectLessonCount = (modules: ModuleLike[]): number =>
  modules.reduce((sum, module) => sum + (module.lessons?.length ?? 0), 0);

const collectLessonMinutes = (modules?: ModuleLike[]): number[] =>
  (modules ?? [])
    .flatMap((module) => module.lessons ?? [])
    .map((lesson) => lesson.duration ?? 0)
    .filter((minutes) => minutes > 0);

export const describeCourseTiming = (course: TimedCourse): CourseTimingParts | null => {
  const totalMinutes = course.duration ?? 0;
  if (totalMinutes <= 0) return null;

  const lessonMinutes = collectLessonMinutes(course.modules);

  // Cards are served without modules, so fall back to the catalogue's typical
  // sitting rather than pretending we can pack lessons we were not given.
  const sessions = lessonMinutes.length
    ? packSessions(lessonMinutes).length
    : Math.max(1, Math.round(totalMinutes / TYPICAL_SESSION_MINUTES));

  const perDay = Math.round(totalMinutes / sessions);
  const weeks = Math.ceil(sessions / SESSIONS_PER_WEEK);

  const cadence =
    sessions > 6
      ? `${weeks} ${weeks === 1 ? 'week' : 'weeks'}`
      : `${sessions} ${sessions === 1 ? 'day' : 'days'}`;

  const lessons =
    course.lessonCount ??
    (course.modules?.length ? collectLessonCount(course.modules) : undefined);
  const modules = course.moduleCount ?? course.modules?.length;

  const structure = [
    lessons ? `${lessons} ${lessons === 1 ? 'lesson' : 'lessons'}` : null,
    modules ? `${modules} ${modules === 1 ? 'module' : 'modules'}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return {
    total: formatDuration(totalMinutes),
    structure,
    schedule: `${cadence} · ${perDay} min a day`,
    sessions,
    weeks,
    pace: `${perDay} min a day`,
    isSingleSitting: totalMinutes <= SINGLE_SITTING_CEILING_MINUTES,
  };
};

/**
 * The line the sales page shows under the title. Short courses keep the
 * "or one afternoon" escape hatch: the schedule makes them read as a programme
 * rather than a file, and the afternoon keeps the positioning claim true for
 * anyone who would rather finish in one go.
 */
export const courseScheduleLine = (course: TimedCourse): string | null => {
  const timing = describeCourseTiming(course);
  if (!timing) return null;

  return timing.isSingleSitting
    ? `${timing.schedule} — or one afternoon`
    : timing.schedule;
};

/**
 * The unit course cards are measured in: whole weeks, always.
 *
 * Cards used to show raw content time — "1h 45m" — which is the one number that
 * makes a paid course look like a file you could have downloaded. Weeks say the
 * same thing as a commitment: the same session model as the sales page and
 * recalibrateCourses.ts, at five sittings a week, each sitting capped where
 * adult attention actually falls off.
 *
 * Unlike `courseScheduleLine`, this never drops to days. A catalogue grid is
 * scanned across, and "5 days" beside "6 weeks" gives the eye two units to
 * reconcile in a row that is already three facts long. Nothing is rounded away
 * to get there: five sittings a week means a five-sitting course *is* one week,
 * which is what 43 of the 60 published courses are.
 */
export const courseWeeksLabel = (course: TimedCourse): string | null => {
  const timing = describeCourseTiming(course);
  if (!timing) return null;

  return `${timing.weeks} ${timing.weeks === 1 ? 'week' : 'weeks'}`;
};
